import { Injectable } from '@angular/core';
import SelectOption from '../../../types/shared/SelectOption';
import { sha512 } from 'js-sha512';

@Injectable({
    providedIn: 'root'
})
export class UtilsService {

    /**
     * Converts a LocalDate string to a date.
     *
     * @param dateString The date string to convert.
     * @returns The date.
     */
    public convertDateStringToDate(dateString: string | undefined): Date | undefined {
        return dateString ? new Date(dateString) : undefined;
    }

    /**
     * Converts a date to a date string in the format `YYYY-MM-DD`, preserving the local date.
     *
     * @param date The date to convert.
     * @returns The formatted date string.
     */
    public convertDateToDateString(date: Date | undefined): string | undefined {
        if (!date) return undefined;

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${ year }-${ month }-${ day }`;
    }

    /**
     * Converts an enum to an array of dropdown options.
     *
     * @param enumObject The enum to convert.
     * @param capitalize Whether to capitalize the labels.
     * @returns An array of select options.
     */
    public createDropdownOptions<T extends object>(enumObject: T, capitalize: boolean = true): SelectOption[] {
        return Object.values(enumObject).map(value => ( {
            label: capitalize ? this.capitalize(value.toString()).replaceAll('_', ' ') :
                value.toString().replaceAll('_', ' '),
            value: value.toString()
        } ));
    }

    /**
     * Capitalizes the first letter of a string and converts the rest to lowercase.
     *
     * @param text The string to capitalize.
     * @returns The capitalized string.
     */
    private capitalize(text: string): string {
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }

    /**
     * Returns the SHA-512 hex string of the given base64-encoded data.
     * Decodes base64 to binary first.
     * @param base64Data Base64-encoded string.
     */
    public sha512HexFromBase64(base64Data: string): string {
        const binaryString = atob(base64Data);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return sha512(bytes);
    }

    /**
     * Compute SHA-512 from a plain text string
     */
    public sha512HexFromText(text: string): string {
        return sha512(text);
    }

    
}
